import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SectionCard } from "../components/SectionCard";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { api, notify } from "../lib/api";
import type { GameDetail } from "../types/domain";

const TARGET_SCORES = [500, 1000, 1500, 2000, 2500, 3000];

export function GameEditPage() {
  const { gameId = "" } = useParams();
  const navigate = useNavigate();
  const { setTrail } = useBreadcrumbs();
  const [game, setGame] = useState<GameDetail | null>(null);
  const [targetScore, setTargetScore] = useState(2000);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    setTrail([]);
    void api.getGame(gameId).then((g) => {
      if (!active) return;
      if (g.mode !== "quick") {
        navigate(`/games/${gameId}`, { replace: true });
        return;
      }
      setGame(g);
      setTargetScore(g.targetScore ?? 2000);
      setTrail([
        { label: "Parties", to: "/games" },
        { label: g.title, to: `/games/${gameId}` },
        { label: "Modifier" },
      ]);
    });
    return () => {
      active = false;
    };
  }, [gameId, navigate, setTrail]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.updateGame(gameId, { targetScore });
      notify("success", "Partie modifiée");
      navigate(`/games/${gameId}`);
    } catch {
      // handled
    } finally {
      setSubmitting(false);
    }
  }

  if (!game) {
    return (
      <div className="sm:max-w-lg mx-auto px-4 py-8">
        <div className="animate-pulse space-y-5">
          <div className="h-7 w-48 rounded bg-gray-200" />
          <div className="h-32 rounded-2xl bg-gray-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="sm:max-w-lg mx-auto px-4 py-6">
      <form className="space-y-5" onSubmit={handleSubmit}>
        <SectionCard title="Score cible">
          <div className="grid grid-cols-6 gap-1.5">
            {TARGET_SCORES.map((s) => (
              <button
                key={s}
                type="button"
                className={`rounded-full border py-2 text-sm font-medium transition ${
                  targetScore === s
                    ? "border-ember bg-ember text-white"
                    : "border-gray-200 bg-white text-ink hover:border-ember/40"
                }`}
                onClick={() => setTargetScore(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </SectionCard>

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={submitting || targetScore === game.targetScore}
        >
          {submitting ? "Enregistrement..." : "Enregistrer"}
        </button>
      </form>
    </div>
  );
}
